import React, { ReactNode, Reducer, useEffect, useReducer } from 'react';
import { AppActions, AppState, DataCookieDefinitionsType } from '../types';
import reducer from './reducer';

export type PaymentProviders = 'braintree' | 'vodafonePay';

export type PaymentMethodsConfig = {
  [key in PaymentProviders]: boolean;
};

export type CurrencySymbolsConfig = {
  [key: string]: string;
}
export interface AppProviderValue {
  state: AppState;
  dispatch(action: AppActions): void;
}

const STORAGE_KEY = '__APP_STATE__';

export const dataCookieDefs: DataCookieDefinitionsType = {
  cookieConsent: "dv_cc"
}

export const defaultState: AppState = {
  billingStatus: {
    billingStatus: '',
    paymentCardNumber: '',
    paymentCardBrand: '',
    purchases: [],
    totalSubscriptionDurationHours: 0,
    userId: '',
    vouchers: [],
  },
  config: {
    theme: {
      mq: {
        desktop: `@media (min-width: 600px)`,
        smallMobile: `@media (max-width: 400px)`,
      },
      colors: {
        accent: '#F00',
        background: {
          paper: '#fff',
          backdrop: '#f5f5f5',
        },
        common: {
          black: '#000',
          darkGrey: '#838383',
          lightGrey: '#f5f5f5',
          grey: '#dadada',
          transparent: 'rgba(0, 0, 0, 0)',
          white: '#fff',
        },
        error: '#c00',
        text: {
          accent: '#F00',
          error: '#c00',
          primary: '#212121',
          secondary: '#F00',
          white: '#fff',
          muted: '#a3a3a3',
          success: '#090',
        },
        success: '#090',
        warning: '#ffc107',
      },
      fonts: {
        families:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";',
        weights: { light: 400, medium: 500, bold: 600 },
        urls: [],
      },
      images: {
        background: '',
        hero: '',
        icon: ''
      },
      radii: {
        accordion: 0,
        basket: 0,
        button: 0,
        card: 0,
        inputs: 0,
      },
      shadows: {
        accordion: '0px 0px transparent',
        card: '0px 0px transparent',
        button: '0px 0px transparent',
        inputs: '0px 0px transparent',
      },
      version: '0.0.1',
    },
    paymentMethods: {
      braintree: true,
      vodafonePay: true,
    },
    currencySymbols: {
    },
    blacklistedPlans: [],
    version: '0.0.0',
  },
  form: {
    emailAddress: '',
    marketingAccepted: false,
    over18: false,
    selectedPlan: null,
    termsAccepted: false,
    userName: '',
    voucherCode: '',
  },
  messages: [],
  params: { config: '', locale: '', voucher: '' },
  plans: [],
  translations: {
    fallback: {},
  },
  user: {
    actorStatus: '',
    apiKey: '',
    apiSecret: '',
    avatar: '',
    campaign: '',
    cdnExpiry: 0,
    cdnSessionId: '',
    countryCode: '',
    displayName: '',
    durationInDays: 0,
    durationInMonths: 0,
    emailAddress: '',
    error: false,
    id: '',
    inviteCode: '',
    marketingAccepted: false,
    organization: '',
    over18: false,
    payment: {
      details: {},
      type: '',
    },
    percentageDiscount: 0,
    pinNumber: '1234',
    productId: 0,
    profileName: '',
    termsAccepted: false,
    trackingAccepted: false,
    subscription: {
      cancelled: false,
      endBillDate: null,
      nextBillDate: '',
      payment: null,
      paymentProvider: '',
      paymentSourceId: '',
      previousBillDate: null,
      productId: 0,
      startBillDate: '',
      userId: '',
    },
    voucherCode: '',
    voucherID: ''
  },
  stateVersion: '0.0.1',
  skipPayment: { skip: false, planId: 0 },
  paymentButtons: {
    showCancelButton: false,
    showRenewButton: false,
    showConfirmButtons: false,
  }
};

export const persistState = (storageKey: string, state: AppState): void => {
  window.localStorage.setItem(storageKey, JSON.stringify(state));
};
export const getIntialState = (storageKey: string): AppState | undefined => {
  const savedState = window.localStorage.getItem(storageKey);
  try {
    if (!savedState) {
      return undefined;
    }
    return JSON.parse(savedState ?? "");
  } catch (e) {
    console.error('Error loading state : ' + storageKey);
    return undefined;
  }
};

const initialState: AppState = getIntialState(STORAGE_KEY) || defaultState;

const initialProviderValue: AppProviderValue = {
  state: initialState,
  dispatch: () => { },
};

export const appStore = React.createContext(initialProviderValue);
const { Provider } = appStore;

let hasClearedVariables = false;

const AppStateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<AppState, any>>(
    reducer,
    initialState
  );

  if(!hasClearedVariables)
  {
    clearNonPersistantStateVariables(state);
    hasClearedVariables = true;
  }

  const providerValue = { state, dispatch };

  useEffect(() => persistState(STORAGE_KEY, state), [state]);

  return <Provider value={providerValue}>{children}</Provider>;
};

//messy way of removing any variables we don't want permanently stored inbetween visits in AppState
//this is a temporary solution as quite frankly state should not be used to store both persistent and non persistent information.
//the fact that it was ever even set up to do this is nuts. future work needs to be done to only keep persistent data in state and everything else gets done at runtime
function clearNonPersistantStateVariables(state: AppState)
{
  state.config = {...defaultState.config};
  state.messages = [...defaultState.messages];
  state.translations = {...defaultState.translations};
}

export default AppStateProvider;
